// Module 10 — imagery providers. R1 (refined): the default/cold-boot path makes NO network call —
// imagery is a self-hosted raster XYZ pyramid (GTA bbox, z≤14) served same-origin, terrain is a flat
// ellipsoid, and there is NO Cesium ion token/asset/endpoint anywhere. Online kinds (osm/satellite/
// photoreal3d) are user-opt-in via setBasemap and are the SOLE permitted runtime network call.
import type { CesiumLike, ImageryProviderLike } from "./cesium-api";
import type { BasemapKind } from "./contract";

export const OFFLINE_TILE_URL = "/basemap/{z}/{x}/{y}.png"; // self-hosted, same-origin; wired in prompt 12

export interface ImageryDescriptor {
  kind: BasemapKind;
  url?: string;
  network: boolean; // false ONLY for offline (cold-boot is fully offline)
  ion: false; // never ion — a typed guarantee
}

function never(x: never): never {
  throw new Error(`unhandled basemap kind: ${String(x)}`);
}

export function imageryDescriptor(kind: BasemapKind, _opts?: { apiKey?: string }): ImageryDescriptor {
  switch (kind) {
    case "offline":
      return { kind, url: OFFLINE_TILE_URL, network: false, ion: false };
    case "osm":
      return { kind, url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png", network: true, ion: false };
    case "satellite":
      return {
        kind,
        url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        network: true,
        ion: false,
      };
    case "photoreal3d":
      // Photorealistic 3D Tiles are a 3D Tileset (a Primitive), NOT a 2D ImageryProvider — the adapter
      // routes this kind to createGooglePhotorealistic3DTileset (direct Google key, NO ion). The
      // descriptor stays for the typed ion:false guarantee + the user-opt-in network flag; it has no url.
      return { kind, network: true, ion: false };
    default:
      return never(kind);
  }
}

export function buildImageryProvider(cesium: CesiumLike, kind: BasemapKind, opts?: { apiKey?: string }): ImageryProviderLike {
  const d = imageryDescriptor(kind, opts);
  switch (kind) {
    case "offline":
      return new cesium.UrlTemplateImageryProvider({ url: d.url ?? OFFLINE_TILE_URL });
    case "osm":
      return new cesium.OpenStreetMapImageryProvider({ url: "https://tile.openstreetmap.org/" });
    case "satellite":
      return new cesium.UrlTemplateImageryProvider({ url: d.url ?? "" });
    case "photoreal3d":
      // not an imagery provider — the adapter's setBasemap tileset branch handles it; never reached.
      throw new Error("photoreal3d is a 3D tileset, not an imagery provider (handled in setBasemap)");
    default:
      return never(kind);
  }
}
